import { useEffect, useRef, useState } from 'react'
import { useDispatch,useSelector } from 'react-redux';
import i18next from 'i18next';
import { apiClient as axios } from '../utils/apiClient';

import {setCurrentCompany} from '../store/currentCompanySlice.js'
import {setCurrentBranch} from '../store/currentBranchSlice.js'
import {getCompanyById} from '../utils/tenantManager.js'
import {setSettings} from '../store/settingSlice.js'
import {setLang} from '../store/langSlice.js'

import {arrayNameByLang,arrayToObjKeyValue, appStorageKey} from '../utils/global.js'
import useLicense from './useLicense.js';

const CENTRAL_SETTINGS_SCOPE = 'global';
const CENTRAL_SELECTION_SCOPE = 'header-selection';
const CENTRAL_SETTINGS_CACHE_PREFIX = 'centralSettingsCache:';

const useStartup = () => {
    
    const dispatch = useDispatch();
    const [startup,setStartup] = useState(false);
    const user = useSelector(state => state.auth).user
    const lang = useSelector(state => state.lang) 
    const initializedRef = useRef(false);

    const safeParse = (value) => {
        if (!value) return null;
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    };
    const userSettings = arrayToObjKeyValue(safeParse(localStorage.getItem('user'))?.userSettings);
    const normalizeLang = (value) => String(value || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const storedPreferredLang = (() => {
        try {
            return localStorage.getItem('preferredLang') || localStorage.getItem('i18nextLng') || localStorage.getItem('appLang');
        } catch {
            return null;
        }
    })();
    const preferredLang = storedPreferredLang ? normalizeLang(storedPreferredLang) : null;

    const {data:dataLicense} = useLicense();

    const getLangText = (nameList, langCode, fallback = '') => {
        return (
            arrayNameByLang(nameList, langCode)?.text ||
            arrayNameByLang(nameList, langCode === 'en' ? 'ar' : 'en')?.text ||
            nameList?.[0]?.text ||
            fallback
        );
    };
    const persistSelectedLang = (langValue) => {
        if (!langValue) return;
        try {
            localStorage.setItem('preferredLang', langValue);
            localStorage.setItem('appLang', langValue);
            localStorage.setItem('i18nextLng', langValue);
        } catch {
        }
    };

    const fetchCentralSelection = async () => {
        if (!user?.tenant_id) return null;
        const token = localStorage.getItem('token');
        if (!token) return null;
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&scope=${CENTRAL_SELECTION_SCOPE}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    validateStatus: () => true
                }
            );
            if (response?.data?.status !== 1) return null;
            return response?.data || null;
        } catch {
            return null;
        }
    };

    const fetchCentralSettings = async (companyId) => {
        if (!user?.tenant_id || !companyId) return null;
        const token = localStorage.getItem('token');
        if (!token) return null;
        const cacheKey = `${CENTRAL_SETTINGS_CACHE_PREFIX}${user.tenant_id}:${companyId}:${CENTRAL_SETTINGS_SCOPE}`;
        const cachedRaw = safeParse(localStorage.getItem(cacheKey));
        try {
            const headers = {
                Authorization: `Bearer ${token}`
            };
            if (cachedRaw?.version) {
                headers['If-None-Match'] = String(cachedRaw.version);
            }
            const response = await axios.get(
                `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&companyId=${companyId}&scope=${CENTRAL_SETTINGS_SCOPE}`,
                { headers, validateStatus: () => true }
            );
            if (response.status === 304) {
                return cachedRaw?.data || null;
            }
            if (response?.data?.status === 1) {
                const nextData = response?.data?.data || null;
                const nextVersion = response?.data?.version ?? cachedRaw?.version ?? 0;
                localStorage.setItem(cacheKey, JSON.stringify({ data: nextData, version: nextVersion }));
                return nextData;
            }
            return cachedRaw?.data || null;
        } catch {
            return cachedRaw?.data || null;
        }
    };
    
    useEffect(() =>{
        const pathArray = window.location.pathname.split('/');
        if(pathArray.length > 2 && pathArray[pathArray.length-2] === 'token'){
            setStartup(true)
        }
    },[dataLicense]);
    
    useEffect(() => {
        const bootstrap = async () => {
            if (!user?._id || initializedRef.current) return;

            const tenantData = safeParse(localStorage.getItem('tenant'));
            if (!tenantData?.companies?.length) return;

            const centralSelectionResponse = await fetchCentralSelection();
            const centralSelection = centralSelectionResponse?.data || null;
            const responseCompany = centralSelectionResponse?.currentCompany;
            const responseBranch = centralSelectionResponse?.currentBranch;

            const storedCompany = safeParse(localStorage.getItem(appStorageKey('selectedCompany')));
            const storedBranch = safeParse(localStorage.getItem(appStorageKey('selectedBranch')));

            const companyId =
                centralSelection?.selectedCompanyId ||
                centralSelection?.companyId ||
                responseCompany?._id ||
                storedCompany?.value ||
                user?.company_id ||
                tenantData?.companies?.[0]?._id;

            const selectedCompany =
                tenantData?.companies?.find((company) => company?._id === companyId) ||
                getCompanyById(user?.company_id) ||
                tenantData?.companies?.[0];

            if (!selectedCompany?._id) return;

            const companyBranches = selectedCompany?.branches || [];
            const branchId =
                centralSelection?.selectedBranchId ||
                centralSelection?.branchId ||
                responseBranch?._id ||
                (storedBranch?.value && companyBranches.some((branch) => branch?._id === storedBranch.value) ? storedBranch.value : null) ||
                (selectedCompany?._id === user?.company_id ? user?.branch_id : null) ||
                companyBranches?.[0]?._id;

            const selectedBranch =
                companyBranches.find((branch) => branch?._id === branchId) ||
                companyBranches?.[0];

            const centralSettings = await fetchCentralSettings(selectedCompany?._id);
            const invoiceSettings = centralSettings?.invoiceSettings || arrayToObjKeyValue(selectedCompany?.settings?.invoice);
            const generalSettings = centralSettings?.generalSettings || arrayToObjKeyValue(selectedCompany?.settings?.generalSettings);
            const defaultLang = generalSettings?.DEFAULT_LANG || userSettings?.DEFAULT_LANG || 'en';
            const centralLang = centralSelection?.selectedLang || centralSelection?.lang || centralSelection?.language || centralSelectionResponse?.currentLanguage;
            const langToApply = centralLang || preferredLang || normalizeLang(lang?.value) || normalizeLang(defaultLang);
            const normalizedLang = normalizeLang(langToApply);
            persistSelectedLang(normalizedLang);

            const companyNameToDisplay =
                responseCompany?.name
                    ? getLangText(responseCompany.name, normalizedLang, 'الشركة')
                    : getLangText(selectedCompany?.name, normalizedLang, 'الشركة');

            const branchNameToDisplay =
                responseBranch?.name
                    ? getLangText(responseBranch.name, normalizedLang, 'الفرع')
                    : getLangText(selectedBranch?.name, normalizedLang, 'الفرع');

            localStorage.setItem(appStorageKey('selectedCompany'), JSON.stringify({
                name: companyNameToDisplay,
                value: selectedCompany._id,
                updatedAt: Date.now()
            }));

            if (selectedBranch?._id) {
                localStorage.setItem(appStorageKey('selectedBranch'), JSON.stringify({
                    name: branchNameToDisplay,
                    value: selectedBranch._id,
                    updatedAt: Date.now()
                }));
            }

            const nextLangLabel = normalizedLang === 'ar' ? 'عربي' : 'English';
            if (lang?.value !== normalizedLang || lang?.label !== nextLangLabel) {
                dispatch(setLang({
                    label: nextLangLabel,
                    value: normalizedLang
                }));
            }
            if (i18next.language !== normalizedLang) {
                i18next.changeLanguage(normalizedLang);
            }

            dispatch(setCurrentCompany({
                name: companyNameToDisplay,
                value: selectedCompany._id
            }));

            if (selectedBranch?._id) {
                dispatch(setCurrentBranch({
                    name: branchNameToDisplay,
                    value: selectedBranch._id
                }));
            }

            dispatch(setSettings({
                invoiceSettings: invoiceSettings || {},
                generalSettings: generalSettings || {}
            }));

            initializedRef.current = true;
            setStartup(true);
        };
        bootstrap();
    }, [dispatch, lang?.label, lang?.value, preferredLang, user?._id, user?.branch_id, user?.company_id, user?.tenant_id]);

    return {startup}
}

export default useStartup
